import React, { useEffect, useRef, useState } from 'react';
import SwiperCore, { Navigation, A11y, Keyboard, Autoplay, FreeMode, Mousewheel } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import ShopByCategorySlide from './ShopByCategorySlide';
import gsap from 'gsap';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import { useReducedMotion } from 'framer-motion';
import ShopByCategoryModal from './ShopByCategoryModal';
import customEventBus from '@common/scripts/libs/eventBus';
import { useTranslation } from 'react-i18next';
import useMediaQueries from '@stone/hooks/useMediaQueries';
gsap.registerPlugin(ScrollToPlugin);
SwiperCore.use([Keyboard, A11y, Autoplay, FreeMode, Navigation, Mousewheel]);
const ShopByCategorySlideshow = (props) => {
    var _a, _b;
    // ANCHOR - HOOKS --------------------------------------------------------------------
    const isReduced = useReducedMotion();
    const { t } = useTranslation();
    const { isDesktop } = useMediaQueries();
    const swiperRef = useRef();
    const duration = useRef(0);
    const startTimer = useRef(null);
    const isRedusced = useReducedMotion();
    // ANCHOR - USE STATE --------------------------------------------------------------------
    const [activeCategory, setActivecategory] = useState(null);
    const [autoplaying, setAutoplaying] = useState(!isReduced);
    const [slideClicked, setSlideClicked] = useState(null);
    // ANCHOR - HOOKS --------------------------------------------------------------------
    const swipeTiming = '500ms';
    const timingSnap = 200;
    // ANCHOR - FUNCTIONS --------------------------------------------------------------------
    const handleElPosition = (el) => {
        const { top, bottom, height } = el.getBoundingClientRect();
        const { innerHeight } = window;
        const distance = Math.abs(innerHeight - bottom);
        const difference = Math.abs(innerHeight - height);
        const percentageDistance = (distance * 100) / height;
        const calculateTiming = (perc, min, max) => {
            perc = perc > 50 ? 50 : perc; //force percentage to 50 in order to set max value
            return min + (perc / 50) * (max - min); //base range on 50 pertentage
        };
        const timing = calculateTiming(percentageDistance, 10, 100);
        return {
            isFullInView: top >= 0 && bottom <= innerHeight,
            timing: timing,
            difference: difference,
        };
    };
    const handleNavigation = (e, type) => {
        // e.preventDefault();
        if (swiperRef.current.autoplay.running) {
            stopAutoplay();
        }
        if (type === 'next') {
            swiperRef.current.slideNext(timingSnap);
        }
        else if (type === 'prev') {
            swiperRef.current.slidePrev(timingSnap);
            // swiperRef.current.slidePrev(timingSnap);
        }
    };
    const onModalClose = () => {
        setActivecategory(null);
        setTimeout(() => {
            if (slideClicked) {
                const button = slideClicked.querySelector('button');
                button.focus();
                setSlideClicked(null);
            }
        }, 300);
        if (!isReduced && autoplaying) {
            setTimeout(() => {
                startAutoplay();
            }, 300);
        }
    };
    const stopAutoplay = (fromButton) => {
        const translation = swiperRef.current.getTranslate();
        if (fromButton) {
            const width = swiperRef.current.width;
            const grid = -swiperRef.current.snapGrid[swiperRef.current.activeIndex];
            const difference = translation - grid;
            duration.current = swiperRef.current.params.speed * (difference / width);
        }
        swiperRef.current.setTranslate(translation);
        swiperRef.current.autoplay.stop();
        swiperRef.current.animating = false;
        customEventBus.emit(`playerstatuschange:${props.uuid}`, { uuid: props.uuid, status: 'play' });
    };
    const startAutoplay = (fromButton) => {
        customEventBus.emit(`playerstatuschange:${props.uuid}`, { uuid: props.uuid, status: 'pause' });
        if (fromButton) {
            swiperRef.current.slideTo(swiperRef.current.activeIndex, duration.current * 2);
        }
        if (startTimer)
            clearTimeout(startTimer.current);
        startTimer.current = setTimeout(() => {
            swiperRef.current.autoplay.start();
        }, 600);
    };
    const onCategorySelect = (id, index) => {
        var _a;
        const realIndex = swiperRef.current.clickedIndex;
        const grid = (_a = swiperRef.current) === null || _a === void 0 ? void 0 : _a.snapGrid[realIndex];
        stopAutoplay();
        setTimeout(() => {
            swiperRef.current.translateTo(-grid, 300);
            setActivecategory(props.categories.find((el) => el.ID === id));
            setTimeout(() => {
                swiperRef.current.slideToLoop(index, 0);
                customEventBus.emit('header:toggleanimation', {});
            }, 600);
        }, 15);
    };
    const onSlideClick = (id, index) => {
        const { isFullInView, timing, difference } = handleElPosition(swiperRef.current.el);
        const header = document.querySelector('header');
        customEventBus.emit('header:toggleanimation', {});
        if (!isFullInView) {
            gsap.to(header, {
                duration: timing / 100,
                y: -header.clientHeight,
                onProgress: (self) => {
                    console.log('progress', self.progress);
                },
            });
            gsap.to(window, {
                duration: timing / 100,
                scrollTo: {
                    y: swiperRef.current.el,
                    offsetY: difference,
                },
                onComplete: () => {
                    onCategorySelect(id, index);
                },
            });
        }
        else {
            onCategorySelect(id, index);
        }
        // swiperRef.current.autoplay.stop();
    };
    const onFocus = () => {
        const { isFullInView, difference } = handleElPosition(swiperRef.current.el);
        stopAutoplay();
        if (!isFullInView) {
            gsap.to(window, {
                duration: 0,
                scrollTo: {
                    y: swiperRef.current.el,
                    offsetY: difference,
                },
            });
        }
    };
    // ANCHOR - USE EFFECT --------------------------------------------------------------------
    // custom management of navigation buttons
    useEffect(() => {
        const btnPrev = document.querySelector('.sbc-navigation--prev');
        const btnNext = document.querySelector('.sbc-navigation--next');
        btnPrev.addEventListener('click', (e) => handleNavigation(e, 'prev'));
        btnNext.addEventListener('click', (e) => handleNavigation(e, 'next'));
        return () => {
            btnPrev.removeEventListener('click', (e) => handleNavigation(e, 'prev'));
            btnNext.removeEventListener('click', (e) => handleNavigation(e, 'next'));
        };
    }, []);
    // handle play/pause from button
    useEffect(() => {
        let _uuid = props.uuid;
        if (swiperRef.current) {
            let handlePauseButton = (data) => {
                if (!swiperRef.current.autoplay.running) {
                    startAutoplay(true);
                    setAutoplaying(true);
                }
                else {
                    stopAutoplay(true);
                    setAutoplaying(false);
                }
                return true;
            };
            customEventBus.on(`toggleplaypause:${_uuid}`, handlePauseButton);
            return () => {
                customEventBus.off(`toggleplaypause:${_uuid}`, handlePauseButton);
            };
        }
    }, []);
    // ANCHOR - RENDER --------------------------------------------------------------------
    return (React.createElement(React.Fragment, null,
        React.createElement(Swiper, { className: "shop-by-category__slideshow", onSwiper: (swiper) => {
                swiperRef.current = swiper;
            }, onResize: () => {
                if (!swiperRef.current.autoplay.running && isDesktop && !activeCategory) {
                    startAutoplay();
                }
            }, onScroll: (swiper, e) => {
                swiper.wrapperEl.style.transitionDuration = swipeTiming;
            }, slidesPerView: 'auto', shortSwipes: !isDesktop, breakpoints: {
                [window.mqObj.lg]: {
                    slidesPerView: 2,
                },
            }, threshold: isDesktop ? 10 : 0, freeMode: { enabled: true }, autoplay: !isRedusced &&
                isDesktop && {
                delay: 0,
                disableOnInteraction: false,
            }, speed: !isRedusced && isDesktop ? 15000 : 300, mousewheel: { forceToAxis: true, thresholdDelta: 20 }, a11y: {
                enabled: true,
                slideRole: 'group',
                itemRoleDescriptionMessage: 'slide',
                slideLabelMessage: '',
                prevSlideMessage: t('label.previouspicture'),
                nextSlideMessage: t('label.nextpicture'),
            }, watchSlidesProgress: true, keyboard: true, loop: true, loopedSlides: props.categories.length, loopAdditionalSlides: props.categories.length, preventClicks: true, preventClicksPropagation: true }, !!((_a = props.categories) === null || _a === void 0 ? void 0 : _a.length) &&
            ((_b = props.categories) === null || _b === void 0 ? void 0 : _b.map((el, i) => (React.createElement(SwiperSlide, { key: el.ID, onClick: (e) => {
                    setSlideClicked(e.target);
                    onSlideClick(el.ID, i);
                }, className: el.ID }, ({ isVisible, isActive, isDuplicate }) => (React.createElement(ShopByCategorySlide, { onClick: () => onSlideClick(el.ID, i), onFocus: onFocus, category: el, index: i, disableFocus: isDesktop ? !isVisible : isActive }))))))),
        React.createElement(ShopByCategoryModal, { activeCategory: activeCategory, closeModal: onModalClose, itemListName: props.itemListName })));
};
export default ShopByCategorySlideshow;
